/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  Package, Plus, Edit3, Trash2, Copy, Users, CreditCard, Wallet, Coins,
  TrendingUp, Shield, Building, Briefcase, Eye, UserPlus, Check, X,
  Loader2, Search, Filter, BarChart3, Settings, Zap, Star, Crown,
  ChevronRight, ArrowRight, Sparkles, Target, Award, Clock, Calendar,
  PieChart, Activity, Info, AlertCircle, CheckCircle2, RefreshCw, ShoppingCart, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StripeCheckoutButton from './StripeCheckoutButton';
import { getToken, getAuthHeader as getStoredAuthHeader } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';
import PlanDialogStep2 from './PlanDialogStep2';


export const GestionPlansPlanDialog = ({ ctx }) => {
  const {
    activeTab, assignForm, assignPlanSearch, assignUserSearch, assignmentSearch,
    assignmentStatusFilter, assignments, dialogStep, editingPlan, filterRole, filterType,
    loading, planForm, plans, saving, searchTerm, selectedPlan, selectedPlanIds,
    showAssignDialog, showDetailDialog, showPlanDialog, users,
    setActiveTab, setAssignForm, setAssignPlanSearch, setAssignUserSearch, setAssignmentSearch,
    setAssignmentStatusFilter, setAssignments, setDialogStep, setEditingPlan, setFilterRole,
    setFilterType, setLoading, setPlanForm, setPlans, setSaving, setSearchTerm, setSelectedPlan,
    setSelectedPlanIds, setShowAssignDialog, setShowDetailDialog, setShowPlanDialog, setUsers,
    fetchAssignments, fetchPlans, fetchUsers, filteredPlans, getAuthHeader, getPricingTypeConfig,
    getRoleConfig, handleAssignPlan, handleBulkDeletePlans, handleBulkToggleActive, handleDeletePlan,
    handleDuplicatePlan, handleSavePlan, openCreateDialog, openDetailDialog, openEditDialog,
    renderPlanPrice, stats, togglePlanSelection, toggleSelectAllPlans,
    PRICING_TYPES, TARGET_ROLES, DEFAULT_PLAN, PLAN_COLORS, getErrorMessage,
  } = ctx;
  return (
      <Dialog open={showPlanDialog} onOpenChange={(open) => { setShowPlanDialog(open); if (!open) { setDialogStep(1); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              {editingPlan ? 'Modifier le plan' : 'Créer un nouveau plan'}
            </DialogTitle>
            <DialogDescription>
              {dialogStep === 1 && "Définissez les informations de base de votre plan"}
              {dialogStep === 2 && "Configurez la tarification de votre plan"}
              {dialogStep === 3 && "Définissez les rôles cibles et les options"}
            </DialogDescription>
          </DialogHeader>

          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-2 py-4">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <button
                  onClick={() => setDialogStep(step)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    dialogStep === step 
                      ? 'bg-purple-600 text-white shadow-lg' 
                      : dialogStep > step 
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {dialogStep > step ? <Check className="h-5 w-5" /> : step}
                </button>
                {step < 3 && (
                  <div className={`w-16 h-1 rounded ${dialogStep > step ? 'bg-purple-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Informations de base */}
            {dialogStep === 1 && (
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium">Nom du plan *</Label>
                  <Input
                    value={planForm.nom}
                    onChange={(e) => setPlanForm({ ...planForm, nom: e.target.value })}
                    placeholder="Ex: Plan Premium, Offre Entreprise..."
                    className="mt-1.5"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <Textarea
                    value={planForm.description}
                    onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                    placeholder="Décrivez les avantages de ce plan..."
                    className="mt-1.5 min-h-[100px]"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Couleur du plan</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PLAN_COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setPlanForm({ ...planForm, couleur: color.value })}
                        className={`w-10 h-10 rounded-lg ${color.class} transition-all ${
                          planForm.couleur === color.value 
                            ? 'ring-4 ring-offset-2 ring-purple-300 scale-110' 
                            : 'hover:scale-105'
                        }`}
                        title={color.label}
                      />
                    ))}
                    <div className="relative">
                      <input
                        type="color"
                        value={planForm.couleur}
                        onChange={(e) => setPlanForm({ ...planForm, couleur: e.target.value })}
                        className="w-10 h-10 rounded-lg cursor-pointer opacity-0 absolute inset-0"
                      />
                      <div 
                        className="w-10 h-10 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"
                        style={{ backgroundColor: planForm.couleur }}
                      >
                        <Plus className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Tarification */}
            {dialogStep === 2 && <PlanDialogStep2 ctx={ctx} />}

            {/* Step 3: Rôles et options */}
            {dialogStep === 3 && (
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium">Rôles cibles *</Label>
                  <p className="text-xs text-gray-500 mb-3">Sélectionnez les utilisateurs qui pourront souscrire à ce plan</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {TARGET_ROLES.map((role) => {
                      const RoleIcon = role.icon;
                      const isSelected = planForm.roles_cibles.includes(role.value);
                      return (
                        <div
                          key={role.value}
                          onClick={() => {
                            if (isSelected) {
                              setPlanForm({
                                ...planForm,
                                roles_cibles: planForm.roles_cibles.filter(r => r !== role.value)
                              });
                            } else {
                              setPlanForm({
                                ...planForm,
                                roles_cibles: [...planForm.roles_cibles, role.value]
                              });
                            }
                          }}
                          className={`
                            p-3 border-2 rounded-xl cursor-pointer transition-all flex items-center gap-2
                            ${isSelected 
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-gray-200 hover:border-purple-200'
                            }
                          `}
                        >
                          <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-purple-600 text-white' : role.bg}`}>
                            <RoleIcon className={`h-4 w-4 ${isSelected ? '' : role.text}`} />
                          </div>
                          <span className={`text-sm font-medium ${isSelected ? 'text-purple-700' : 'text-gray-600'}`}>
                            {role.label}
                          </span>
                          {isSelected && <Check className="h-4 w-4 text-purple-600 ml-auto" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      <div>
                        <Label className="font-medium">Plan actif</Label>
                        <p className="text-xs text-gray-500">Visible et disponible à la souscription</p>
                      </div>
                    </div>
                    <Switch
                      checked={planForm.is_active}
                      onCheckedChange={(v) => setPlanForm({ ...planForm, is_active: v })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Crown className="h-5 w-5 text-amber-500" />
                      <div>
                        <Label className="font-medium">Plan par défaut</Label>
                        <p className="text-xs text-gray-500">Assigné automatiquement aux nouveaux utilisateurs</p>
                      </div>
                    </div>
                    <Switch
                      checked={planForm.is_default}
                      onCheckedChange={(v) => setPlanForm({ ...planForm, is_default: v })}
                    />
                  </div>
                </div>

                {/* Ordre d'affichage */}
                <div>
                  <Label className="text-sm font-medium">Ordre d&apos;affichage</Label>
                  <Input
                    type="number"
                    min="0"
                    value={planForm.ordre_affichage}
                    onChange={(e) => setPlanForm({ ...planForm, ordre_affichage: parseInt(e.target.value) || 0 })}
                    className="mt-1.5 w-32"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Les plans avec un ordre plus bas apparaissent en premier</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 gap-2">
            {dialogStep > 1 && (
              <Button variant="outline" onClick={() => setDialogStep(dialogStep - 1)}>
                Précédent
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
              Annuler
            </Button>
            {dialogStep < 3 ? (
              <Button onClick={() => setDialogStep(dialogStep + 1)} className="bg-purple-600 hover:bg-purple-700">
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSavePlan} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                {editingPlan ? 'Mettre à jour' : 'Créer le plan'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default GestionPlansPlanDialog;
